export const techcoreSessionItems = [
	// Set 1 – MCQ
	{
		type: "SET",
		stimulus: {
			id: 40200,
			content: {
				instruction:
					"Listen to the audio and select the correct answer from the options provided.",
				passage: {
					title:
						"DDoS Attack During a Product Launch – Accountability in Action",
					paragraphs: [
						{
							text: "During a major product launch, our company’s website was struck by a massive DDoS attack. Your delayed response worsened the situation, resulting in a significant loss of customer trust, revenue, and damage to our credibility. The CEO has left a voicemail below:",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "audio5",
						url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/acme/techcore.mp3",
						type: "audio",
						transcript: `This is Alex, CEO of ACME Corp.

I've gotta be honest with you—what happened today was really disappointing.

Our website was hit by a DDoS attack… and the response, you know, it just came way too late.

That delay? It cost us.
Customer trust. Revenue. And, honestly, our credibility.

So, I need to understand—what exactly went wrong?

What steps have you taken so far to address it?

And more importantly, what's the plan to make sure this doesn't happen again?

I need a detailed action plan—on my desk—by the end of the day, alright?`,
					},
				],
			},
			metadata: {
				type: "READING",
				skill: "READING",
				title: "Reading Stimulus",
			},
		},
		items: [
			{
				id: 40201,
				type: "MCSS",
				content: {
					parentId: 40200,
					parentType: "SET",
					stem: "After receiving a voicemail from the CEO regarding the DDoS attack, what is the most critical immediate action you should take?",
					choices: [
						{
							id: "e1f2a3b4-c5d6-7890-abcd-ef1234567890",
							stem: "Activate a DDoS mitigation service immediately",
							text: "Activate a DDoS mitigation service immediately",
							isCorrect: true,
							explanation: `
A DDoS attack during a product launch represents a high-stakes incident that demands **rapid intervention** to minimize operational disruption and financial loss. Quick action is essential to protect the company's reputation and customer trust.

**Activating a DDoS mitigation service immediately** is the most effective response because it rapidly filters out **malicious traffic** and restores normal operations. This decisive action minimizes downtime, preserves customer trust, and prevents further revenue loss by directly addressing the attack.

**Increasing server capacity incrementally** or **running diagnostics while waiting for IT support** delays the necessary response and does not effectively mitigate the attack; similarly, **simply informing the CEO** without taking immediate technical action leaves the threat uncontained. These options fail to provide the swift resolution required in such critical incidents.

**Key Takeaways:**
- **Immediate technical intervention** is critical to contain the impact of a DDoS attack.
- **Proactive and decisive measures** are necessary to quickly restore normal operations and protect revenue.
- **DDoS mitigation services** provide real-time traffic analysis and filtering capabilities.
- **Response time** directly correlates with financial impact and customer trust.
- **Automated protection systems** can detect and mitigate attacks before manual intervention.

**References:**
- National Institute of Standards and Technology. (2012). *Computer security incident handling guide* (NIST Special Publication 800-61 Revision 2). Retrieved from https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-61r2.pdf
- CERT Division, Carnegie Mellon University. (2020). *Best practices for cybersecurity incident response*. Retrieved from https://resources.sei.cmu.edu/library/asset-view.cfm?assetid=536099
- Cloudflare. (2023). *DDoS attack trends and mitigation strategies*. Retrieved from https://www.cloudflare.com/ddos/`,
						},
						{
							id: "f2e3d4c5-b6a7-8901-cdef-0123456789ab",
							stem: "Increase server capacity incrementally",
							text: "Increase server capacity incrementally",
							isCorrect: false,
						},
						{
							id: "a3b4c5d6-e7f8-9012-abcd-ef2345678901",
							stem: "Run diagnostics while waiting for IT support",
							text: "Run diagnostics while waiting for IT support",
							isCorrect: false,
						},
						{
							id: "b4c5d6e7-f8a9-0123-bcde-f34567890123",
							stem: "Inform the CEO and wait for further instructions",
							text: "Inform the CEO and wait for further instructions",
							isCorrect: false,
						},
					],
					instruction: "Choose the best answer",
					highlights: null,
					media: null,
				},
				tags: [
					{
						id: 6479,
						type: "Skill",
						label: "Incident Response",
						breadcrumb: "Incident Response",
					},
				],
				metadata: {
					type: "MCSS",
					skill: "READING",
					title: "Multiple Choice Single Selection",
					topics: ["DDoS Attack"],
				},
				response: {
					maximumScore: 1,
					correctResponse: [{ id: "e1f2a3b4-c5d6-7890-abcd-ef1234567890" }],
					numChoicesRequired: { max: 1, min: 1 },
				},
			},
		],
	},
	// Set 2 – Writing (Email Response)
	{
		type: "SET",
		stimulus: {
			id: 40210,
			content: {
				instruction: "Listen to the audio and then respond as instructed.",
				passage: {
					title: "DDoS Attack Incident – Draft Your Email Response",
					paragraphs: [
						{
							text: "During a major product launch, our company’s website was struck by a massive DDoS attack. Your delayed response worsened the incident, resulting in significant losses in customer trust and revenue. The CEO has left a voicemail below:",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "audio6",
						url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/acme/techcore.mp3",
						type: "audio",
						transcript: `This is Alex, CEO of ACME Corp.

I've gotta be honest with you—what happened today was really disappointing.

Our website was hit by a DDoS attack… and the response, you know, it just came way too late.

That delay? It cost us.
Customer trust. Revenue. And, honestly, our credibility.

So, I need to understand—what exactly went wrong?

What steps have you taken so far to address it?

And more importantly, what's the plan to make sure this doesn't happen again?

I need a detailed action plan—on my desk—by the end of the day, alright?`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_STIMULUS",
				skill: "WRITING",
				title: "Role-Play Writing Stimulus",
				topics: ["Network Monitoring"],
			},
		},
		items: [
			{
				id: 40211,
				versionId: 1,
				content: {
					parentId: 40210,
					parentType: "SET",
					stem: "Based on the CEO’s voicemail, draft an email response that explains what went wrong, outlines the immediate corrective measures you will implement, and describes how you plan to prevent similar incidents in the future. Justify your approach.",
					choices: [],
					instruction: "Write your response in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 15,
					subskillsSchema: [
						{
							name: "Clarity and Organization",
							icon: "file-document-outline",
							description: "Structure your response clearly.",
							maxScore: 5,
						},
						{
							name: "Crisis Management",
							icon: "shield-check-outline",
							description: "Effectively address the incident.",
							maxScore: 5,
						},
						{
							name: "Justification",
							icon: "check-circle-outline",
							description: "Provide rationale for your decisions.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Role-Play Writing Response",
					topics: ["Network Monitoring"],
				},
				score: null,
			},
		],
	},
	// Set 3 – Marking
	{
		type: "SET",
		stimulus: {
			id: 40220,
			content: {
				instruction:
					"Listen to the audio and then complete the role-play marking task.",
				passage: {
					title: "Network Traffic Analysis",
					paragraphs: [
						{
							text: "During a major product launch, our company’s website was struck by a massive DDoS attack. Your delayed response worsened the incident, resulting in significant losses in customer trust, revenue, and damage to our credibility. The CEO has left a voicemail below:",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "audio7",
						url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/acme/techcore.mp3",
						type: "audio",
						transcript: `This is Alex, CEO of ACME Corp.

I've gotta be honest with you—what happened today was really disappointing.

Our website was hit by a DDoS attack… and the response, you know, it just came way too late.

That delay? It cost us.
Customer trust. Revenue. And, honestly, our credibility.

So, I need to understand—what exactly went wrong?

What steps have you taken so far to address it?

And more importantly, what's the plan to make sure this doesn't happen again?

I need a detailed action plan—on my desk—by the end of the day, alright?`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_MARKING_ANSWER_STIMULUS",
				skill: "MARKING",
				title: "Integrated Marking Answer Stimulus",
				topics: ["Network Traffic"],
			},
		},
		items: [
			{
				id: 40221,
				versionId: 1,
				content: {
					parentId: 40220,
					parentType: "SET",
					stem: "Review the attached network traffic chart. Circle the section where a sudden, steep spike in traffic occurs.",
					choices: [],
					instruction: "Circle and annotate the image as instructed.",
					media: [
						{
							id: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/graph.png",
							url: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/graph.png",
							type: "image",
						},
					],
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Marking",
						breadcrumb: "Marking",
					},
				],
				response: {
					maximumScore: 10,
					subskillsSchema: [
						{
							name: "Accuracy",
							icon: "crosshairs-gps",
							description: "Precisely identify the abnormal spike.",
							maxScore: 5,
						},
						{
							name: "Speed",
							icon: "timer-outline",
							description: "Identify the error area quickly.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "MARKING_ANSWER",
					skill: "MARKING",
					title: "Network Traffic Annotation",
					topics: ["Network Traffic"],
				},
				score: null,
			},
		],
	},
	// Set 4 – Audio
	{
		type: "SET",
		stimulus: {
			id: 40230,
			content: {
				instruction: "Listen to the audio and record your answer.",
				passage: {
					title: "Audio Scenario: Addressing the CEO",
					paragraphs: [
						{
							text: "During a major product launch, our company’s website was struck by a massive DDoS attack. Your delayed response worsened the incident, resulting in significant losses in customer trust, revenue, and credibility. The CEO has left a voicemail below:",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "audio8",
						url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/acme/techcore.mp3",
						type: "audio",
						transcript: `This is Alex, CEO of ACME Corp.

I've gotta be honest with you—what happened today was really disappointing.

Our website was hit by a DDoS attack… and the response, you know, it just came way too late.

That delay? It cost us.
Customer trust. Revenue. And, honestly, our credibility.

So, I need to understand—what exactly went wrong?

What steps have you taken so far to address it?

And more importantly, what's the plan to make sure this doesn't happen again?

I need a detailed action plan—on my desk—by the end of the day, alright?`,
					},
				],
			},
			metadata: {
				type: "LS_STIMULUS",
				skill: "SPEAKING",
				title: "Listen Speak Stimulus",
				topics: ["Accountability"],
			},
		},
		items: [
			{
				id: 40231,
				type: "AUDIO_INPUT_EMOTION",
				content: {
					parentId: 40230,
					parentType: "SET",
					stem: "Record an audio response in which you take full responsibility for the delayed response to the DDoS attack, explain what went wrong, outline your corrective measures, and detail your long-term plan to improve incident response times.",
					choices: [],
					instruction: "Record your answer.",
					media: null,
				},
				tags: [
					{
						id: 6481,
						type: "Skill",
						label: "Speaking",
						breadcrumb: "Speaking",
					},
				],
				response: {
					maximumScore: 30,
					maximumDuration: 40,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Communication & Interpersonal Skills",
							icon: "message-outline",
							description: "Demonstrate effective verbal communication.",
						},
						{
							maxScore: 5,
							name: "Responsibility",
							icon: "account-check-outline",
							description: "Show accountability for the incident.",
						},
						{
							maxScore: 5,
							name: "Clarity",
							icon: "microphone-outline",
							description: "Speak clearly and coherently.",
						},
						{
							maxScore: 5,
							name: "Professionalism",
							icon: "briefcase-outline",
							description: "Maintain a professional tone.",
						},
						{
							maxScore: 5,
							name: "Solution Orientation",
							icon: "arrow-right-circle-outline",
							description: "Present clear corrective actions.",
						},
						{
							maxScore: 5,
							name: "Empathy",
							icon: "heart-outline",
							description:
								"Convey understanding and empathy toward stakeholders.",
						},
					],
				},
				metadata: {
					type: "AUDIO_INPUT_EMOTION",
					skill: "SPEAKING",
					title: "Audio Response Item",
					topics: ["Accountability"],
				},
				score: null,
			},
		],
	},
	// Set 5 – Writing (Post-Incident Report)
	{
		type: "SET",
		stimulus: {
			id: 40240,
			content: {
				instruction: "Listen to the audio and then respond as instructed.",
				passage: {
					title: "DDoS Attack Incident – Post-Incident Report",
					paragraphs: [
						{
							text: "During a major product launch, our company’s website was struck by a massive DDoS attack. Your delayed response worsened the incident, resulting in significant losses in customer trust, revenue, and damage to our credibility. The CEO has left a voicemail below. Based on this incident, compose a comprehensive post-incident report that includes a timeline of events, an assessment of the impact, the corrective actions taken, and recommendations for updating response protocols to prevent similar incidents in the future.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "audio6a",
						url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/acme/techcore.mp3",
						type: "audio",
						transcript: `This is Alex, CEO of ACME Corp.

I've gotta be honest with you—what happened today was really disappointing.

Our website was hit by a DDoS attack… and the response, you know, it just came way too late.

That delay? It cost us.
Customer trust. Revenue. And, honestly, our credibility.

So, I need to understand—what exactly went wrong?

What steps have you taken so far to address it?

And more importantly, what's the plan to make sure this doesn't happen again?

I need a detailed action plan—on my desk—by the end of the day, alright?`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_STIMULUS",
				skill: "WRITING",
				title: "Post-Incident Report Writing Stimulus",
				topics: ["Network Monitoring"],
			},
		},
		items: [
			{
				id: 40241,
				versionId: 1,
				content: {
					parentId: 40240,
					parentType: "SET",
					stem: "Based on the CEO’s voicemail, compose a comprehensive post-incident report that includes a timeline of events leading up to the DDoS attack, an assessment of the impact on customer trust and revenue, the corrective actions taken, and detailed recommendations for updating response protocols to prevent future incidents. Justify your recommendations.",
					choices: [],
					instruction: "Write your response in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 20,
					subskillsSchema: [
						{
							name: "Organization",
							icon: "file-tree-outline",
							description: "Organize your report logically.",
							maxScore: 5,
						},
						{
							name: "Accountability",
							icon: "check-circle-outline",
							description: "Demonstrate responsibility and transparency.",
							maxScore: 5,
						},
						{
							name: "Actionable Recommendations",
							icon: "lightbulb-outline",
							description: "Offer practical measures for prevention.",
							maxScore: 5,
						},
						{
							name: "Detail Orientation",
							icon: "file-document-outline",
							description: "Include all relevant details in your report.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Post-Incident Report Response",
					topics: ["Network Monitoring"],
					taskType: "default",
				},
				score: null,
			},
		],
	},
];
